import React from 'react';
import { useNavigate } from 'react-router-dom';
import useGradingStore from '@/components/staffer/Grading/useGradingStore';
import GradingSelect from '@/components/shared/Grading/Navigation/GradingSelect';
import { IClassroom, IStudent } from '@/components/staffer/Grading/types';
import ReactSelect5, { Option as GradingSelectOption } from '@/components/common/ReactSelect5';
import { gradingNavigationStringParams } from '@/components/staffer/Grading/utils';
import ArrowNavigator from './ArrowNavigator';

const StudentSelect = ({ selectedStudent, navigableStudentOptions, onStudentChange, studentOptionsData }) => {
  const label = 'Student';
  const toggleId = 'student-toggle';

  return (
    <ArrowNavigator
      containerClass="tw-max-w-[250px]"
      currentValue={selectedStudent?.value || ''}
      isDisabled={navigableStudentOptions.length <= 1}
      label={label}
      labelClass="tw-text-xs"
      navigableOptions={navigableStudentOptions}
      toggleId={toggleId}
      onChange={onStudentChange}
    >
      <ReactSelect5
        className="tw-w-full"
        id={toggleId}
        options={studentOptionsData}
        value={selectedStudent}
        onChange={(opt: GradingSelectOption) => onStudentChange(opt.value)}
      />
    </ArrowNavigator>
  );
};

const TopRowFilters = ({ classrooms }) => {
  const { assignment, classroom, isQuestionMode, questionId, setGradingStore, student } = useGradingStore();
  const navigate = useNavigate();
  let selectedClassroom: GradingSelectOption;
  let selectedStudent: GradingSelectOption;
  let studentsOfSelectedClassroom: IStudent[] = [];

  const classroomOptionsData = classrooms.map((option: IClassroom) => {
    const data = {
      isDisabled: !option.students.some(s => s.submission_id),
      label: option.full_name,
      value: option.id.toString(),
    };
    const isClassroomForQuestionMode = isQuestionMode && classroom && option.id === classroom.id;
    const isClassroomOfSelectedStudent = !isQuestionMode && student && option.students.find(s => s.id === student.id);

    if (isClassroomForQuestionMode || isClassroomOfSelectedStudent) {
      selectedClassroom = data;
      studentsOfSelectedClassroom = option.students;
    }

    return data;
  });

  const navigableStudentOptions = [];
  const studentOptionsData = studentsOfSelectedClassroom
    .sort((a, b) => a.last_name.localeCompare(b.last_name))
    .map((st) => {
      const data = {
        isDisabled: !st.submission_id,
        label: [st.first_name, st.last_name].filter(a => a != null).join(' '),
        value: st.id.toString(),
      };
      if (st.submission_id) navigableStudentOptions.push(data);
      if (!isQuestionMode && st.id === student.id) selectedStudent = data;

      return data;
    });

  const onClassroomChange = (value) => {
    if (isQuestionMode) {
      const newClassroom = assignment.classrooms.find(c => c.id.toString() === value);
      setGradingStore({ classroom: newClassroom });
      navigate(`/${assignment.id}/questions/${questionId}?${gradingNavigationStringParams(newClassroom.id)}`);
    } else {
      const classroomOption = classrooms.find(c => c.id.toString() === value);
      const submissionId = classroomOption.students.find(s => s.submission_id)?.submission_id;
      navigate(`/${submissionId}?${gradingNavigationStringParams(value)}`);
    }
  };

  const onStudentChange = (value) => {
    const submissionId = studentsOfSelectedClassroom.find(s => s.id.toString() === value)?.submission_id;
    if (submissionId) {
      navigate(`/${submissionId}?${gradingNavigationStringParams()}`);
    }
  };

  return (
    <>
      <GradingSelect
        aria-label="dropdown-toggle"
        defaultValue={selectedClassroom}
        id="classroom-toggle"
        label="Classroom"
        labelClass="tw-text-xs"
        options={classroomOptionsData}
        selectClass="tw-ml-1"
        onChange={({ value }) => onClassroomChange(value)}
      />

      {!isQuestionMode && (
        <StudentSelect
          navigableStudentOptions={navigableStudentOptions}
          selectedStudent={selectedStudent}
          studentOptionsData={studentOptionsData}
          onStudentChange={onStudentChange}
        />
      )}
    </>
  );
};

export default TopRowFilters;
